import React, { useContext, useState } from "react"
import "twin.macro"
import { useTranslation } from "next-i18next"
import { QuestionCard } from "../questionCard"
import {
  Platform,
  QuestionDetailFragment,
  useGetSortedAnswersLazyQuery,
  useGetSortedQuestionsLazyQuery,
} from "../../graphql/generated/graphql"
import { AnswersZeroState, QuestionsZeroState } from "./zeroState"
import { Button, ButtonTheme } from "../formElements/button"
import { useEffectAsync } from "../../utils/hooks/useEffectAsync"
import { ReactComponent as SpinnerImage } from "../../assets/icons/spinner.svg"
import { RedirectContext } from "../../utils/context"
import { TabState } from "."
import { Filter } from "./filters"
import { AnswerCard } from "../answerCard"
import { Search } from "./search"

export interface GetQuestionsProps {
  platform?: Platform
  page: number
  sort: QuestionSort
  videoId?: string
}
export enum QuestionSort {
  New = "New",
  Old = "Old",
  Popular = "Popular",
}
export interface QuestionsProps {
  isUser: boolean
  username: string
}
export const Questions = ({ isUser, username }: QuestionsProps) => {
  const { setData, getData } = useContext(RedirectContext)
  const redirectData = getData().data
  const [page, setPage] = useState(1)
  const [platform, setPlatfrom] = useState<Platform>()
  const [sort, setSort] = useState(QuestionSort.Popular)
  const [videoId, setVideoId] = useState<string>()
  const [questions, setQuestions] = useState<QuestionDetailFragment[]>([])
  const [showLoadMore, setShowLoadMore] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const { t } = useTranslation("profile")
  const [getSortedQuestions] = useGetSortedQuestionsLazyQuery()

  const setResponse = (response: any) => {
    const { questions, nextPage } = response.data.SortedQuestions
    setQuestions(questions)
    setShowLoadMore(nextPage)
  }

  const reset = async () => {
    setIsLoading(true)
    setShowLoadMore(false)
    const response = await getSortedQuestions({
      variables: { username, page: 1, sort: QuestionSort.Popular },
    })
    setResponse(response)
    setSort(QuestionSort.Popular)
    setVideoId(undefined)
    setPlatfrom(undefined)
    setPage(1)
    setIsLoading(false)
  }

  const getQuestions = async ({ platform, page, sort, videoId }: GetQuestionsProps) => {
    const response = await getSortedQuestions({
      variables: { username, platform, page, sort, videoId },
    })
    setResponse(response)
    setPlatfrom(platform)
    setPage(page)
    setSort(sort)
    setVideoId(videoId)
  }

  useEffectAsync(
    async () => {
      const { index, activeTab, platform, page, sort, videoId } = redirectData
      if (activeTab == TabState.Questions) {
        setIsLoading(true)
        setShowLoadMore(false)
        await getQuestions({ platform, page, sort, videoId })
        setIsLoading(false)
        const clickedElement = document.getElementById(`question-${index}`)
        clickedElement?.scrollIntoView()
      } else {
        await reset()
      }
    },
    [],
    reset,
  )

  return (
    <>
      <div tw="flex flex-wrap items-center tablet-lg:(flex-nowrap)">
        <Search {...{ getQuestions, username }} />
        <Filter {...{ getQuestions }} />
      </div>
      {isLoading ? (
        <span tw="h-[200px] w-full flex items-center justify-center">
          <SpinnerImage tw="animate-spin h-14 w-14" />
        </span>
      ) : questions.length > 0 ? (
        <div tw="flex flex-col flex-nowrap tablet:(flex-row flex-wrap justify-between items-center)">
          {questions.map((question, index) => (
            <div
              onClick={() => setData({ index, activeTab: TabState.Questions, platform, page, sort, videoId })}
              id={`question-${index}`}
              tw="w-full py-2 tablet:(py-3.5)"
              key={index}
              data-testid={`question-${question.state.toLowerCase()}`}
            >
              <QuestionCard question={question} linked={true} referringProfileUsername={username} />
            </div>
          ))}
        </div>
      ) : (
        <QuestionsZeroState {...{ isUser }} />
      )}
      {showLoadMore && (
        <div tw="w-full flex justify-center">
          <Button
            theme={ButtonTheme.PrimaryOutline}
            onClickAsync={async () => await getQuestions({ platform, page: page + 1, sort, videoId })}
            action={t("load_more")}
          />
        </div>
      )}
    </>
  )
}

export const Answers = ({ isUser, username }: QuestionsProps) => {
  const { setData, getData } = useContext(RedirectContext)
  const redirectData = getData().data
  const [page, setPage] = useState(1)
  const [platform, setPlatfrom] = useState<Platform>()
  const [sort, setSort] = useState(QuestionSort.Popular)
  const [answers, setAnswers] = useState<QuestionDetailFragment[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showLoadMore, setShowLoadMore] = useState(false)
  const { t } = useTranslation("profile")
  const [getSortedAnswers] = useGetSortedAnswersLazyQuery()

  const setResponse = (response: any) => {
    const { answers, nextPage } = response.data.SortedAnswers
    setAnswers(answers)
    setShowLoadMore(nextPage)
  }

  const reset = async () => {
    setIsLoading(true)
    setShowLoadMore(false)
    const response = await getSortedAnswers({
      variables: { username, page: 1, sort: QuestionSort.Popular },
    })
    setResponse(response)
    setPlatfrom(undefined)
    setSort(QuestionSort.Popular)
    setPage(1)
    setIsLoading(false)
  }

  const getAnswers = async ({ platform, page, sort }: GetQuestionsProps) => {
    const response = await getSortedAnswers({
      variables: { username, platform, page, sort },
    })
    setResponse(response)
    setPlatfrom(platform)
    setPage(page)
    setSort(sort)
  }

  useEffectAsync(
    async () => {
      const { sort, platform, page, index, activeTab } = redirectData
      if (activeTab == TabState.Answers) {
        setIsLoading(true)
        setShowLoadMore(false)
        await getAnswers({ platform, page, sort })
        setIsLoading(false)
        const clickedElement = document.getElementById(`answer-${index}`)
        clickedElement?.scrollIntoView()
      } else {
        await reset()
      }
    },
    [],
    reset,
  )

  return (
    <>
      <div tw="flex tablet-lg:justify-end">
        <Filter {...{ getQuestions: getAnswers }} />
      </div>
      {isLoading ? (
        <span tw="h-[200px] w-full flex items-center justify-center">
          <SpinnerImage tw="animate-spin h-14 w-14" />
        </span>
      ) : answers.length > 0 ? (
        <div tw="flex flex-col flex-nowrap tablet:(flex-row flex-wrap justify-between items-center)">
          {answers.map((question, index) => (
            <div
              onClick={() => setData({ sort, platform, page, index, activeTab: TabState.Answers })}
              id={`answer-${index}`}
              tw="w-full py-2 tablet:(py-3.5 w-[48%])"
              key={index}
              data-testid={`question-${question.state.toLowerCase()}`}
            >
              <AnswerCard
                {...{
                  question,
                  isReactable: false,
                  useEmojiSmall: true,
                  isPlayable: false,
                  referringProfileUsername: username,
                }}
              />
            </div>
          ))}
        </div>
      ) : (
        <AnswersZeroState {...{ isUser }} />
      )}
      {showLoadMore && (
        <div tw="w-full flex justify-center">
          <Button
            theme={ButtonTheme.PrimaryOutline}
            onClickAsync={async () => await getAnswers({ platform, page: page + 1, sort })}
            action={t("load_more")}
          />
        </div>
      )}
    </>
  )
}
